import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { requestMagicLink, ApiError } from "@/lib/api-client";

const TURNSTILE_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const TURNSTILE_TEST_SITEKEY = "1x00000000000000000000AA";

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  "error-callback"?: () => void;
  "expired-callback"?: () => void;
  theme?: "light" | "dark" | "auto";
}

interface TurnstileGlobal {
  render: (el: HTMLElement, opts: TurnstileRenderOptions) => string;
  reset: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function loadTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src^="https://challenges.cloudflare.com/turnstile/v0/api.js"]`
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("turnstile failed to load")),
        { once: true }
      );
      return;
    }
    const script = document.createElement("script");
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("turnstile failed to load"));
    document.head.appendChild(script);
  });
}

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>("");
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const turnstileElRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Vite injects env vars at build. Use a safe cast so the test tsconfig
  // (which doesn't include vite-env.d.ts) still type-checks.
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};
  const siteKey = env.VITE_TURNSTILE_SITE_KEY || TURNSTILE_TEST_SITEKEY;

  useEffect(() => {
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !turnstileElRef.current || !window.turnstile) return;
        if (widgetIdRef.current) return; // already rendered
        widgetIdRef.current = window.turnstile.render(
          turnstileElRef.current,
          {
            sitekey: siteKey,
            theme: "dark",
            callback: (token: string) => {
              setTurnstileToken(token);
            },
            "expired-callback": () => setTurnstileToken(""),
            "error-callback": () => setTurnstileToken(""),
          }
        );
      })
      .catch(() => {
        // Silent fail — test key path will still let dev click submit once
        // turnstile is manually injected. Do not block the form.
      });
    return () => {
      cancelled = true;
    };
  }, [siteKey]);

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit = emailValid && turnstileToken.length > 0 && !submitting;

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await requestMagicLink(email.trim(), turnstileToken);
      navigate("/login/check", { state: { email: email.trim() } });
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Something went wrong. Please try again.");
      if (window.turnstile && widgetIdRef.current) {
        window.turnstile.reset(widgetIdRef.current);
      }
      setTurnstileToken("");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <form
        className="auth-card"
        onSubmit={handleSubmit}
        data-testid="login-form"
      >
        <h1 className="auth-title">Sign in to Mono</h1>
        <p className="auth-sub">
          Enter your email and we'll send you a secure link.
        </p>

        <div className="auth-field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@monoskin.health"
            data-testid="login-email"
          />
        </div>

        <div
          className="auth-turnstile"
          ref={turnstileElRef}
          data-testid="turnstile-container"
        >
          {/* Turnstile widget renders here. Tests can populate token via
              the controlled test input below (kept visually hidden). */}
          <input
            type="text"
            aria-hidden="true"
            tabIndex={-1}
            data-testid="turnstile-token-test-input"
            value={turnstileToken}
            onChange={(e) => setTurnstileToken(e.target.value)}
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              opacity: 0,
              pointerEvents: "none",
            }}
          />
        </div>

        {error && (
          <div className="auth-error" data-testid="login-error">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="auth-button"
          disabled={!canSubmit}
          data-testid="login-submit"
        >
          {submitting ? "Sending…" : "Send link"}
        </button>
      </form>
    </div>
  );
}
